import { ReactNode } from "react";

interface Classes {
  wrapper?: string;
  input?: string;
}

interface InputProps {
  id?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  type?: string;
  value?: string;
  inputKey?: string;
  placeholder?: string;
  disabled?: boolean;
  onChange?: Function;
  onFocus?: Function;
  classes?: Classes;
  autoFocus?: boolean;
  refEl?: any;
  error?: any;
  validator?: any;
  readOnly?: any;
  maxLength?: number;
  minLength?: number;
  accept?: string;
}

export const TextField = ({
  id,
  prefix,
  suffix,
  type = "text",
  value,
  inputKey,
  placeholder = "",
  disabled = false,
  onChange,
  classes: { wrapper = "", input = "" },
  autoFocus,
  onFocus,
  refEl,
  error,
  validator = (val) => val,
  readOnly = false,
  maxLength,
  minLength,
}: InputProps) => {
  const identifier = id || inputKey;
  const onInputChange = (ev) => onChange(validator(ev.target.value), identifier, ev);
  return (
    <div className={`form-group d-flex flex-wrap ${wrapper}`}>
      {prefix}
      <div className="w-100">
        <input
          type={type}
          placeholder={placeholder}
          className={`form-control ${input} ${error ? "input-error" : ""}`}
          id={identifier}
          disabled={disabled}
          value={value}
          onChange={onInputChange}
          onFocus={onFocus}
          autoFocus={autoFocus}
          ref={refEl}
          readOnly={readOnly}
          maxLength={maxLength}
          minLength={minLength}
        />
        <div className="input-animation"></div>
      </div>
      {error && <span className="error-message text-danger py-2">{error}</span>}
      {suffix}
    </div>
  );
};

export const FileField = ({
  id,
  prefix,
  suffix,
  type = "file",
  value,
  inputKey,
  placeholder = "",
  disabled = false,
  onChange,
  classes: { wrapper = "", input = "" },
  refEl,
  error,
  accept = "",
}: InputProps) => {
  const identifier = id || inputKey;
  const onInputChange = (ev) => {
    onChange(ev.target.files, identifier, ev);
    ev.target.value = "";
  };
  return (
    <div className={`form-group d-flex flex-wrap ${wrapper}`}>
      {prefix}
      <div className="w-100">
        <input
          type={type}
          placeholder={placeholder}
          className={`form-control ${input} ${error ? "input-error" : ""}`}
          id={identifier}
          disabled={disabled}
          value={value}
          onChange={onInputChange}
          ref={refEl}
          draggable
          accept={accept}
        />
        <div className="input-animation"></div>
      </div>
      {suffix}
      {error && <span className="error-message text-danger py-2">{error}</span>}
    </div>
  );
};

export const TextAreaField = ({
  id,
  prefix,
  suffix,
  value,
  inputKey,
  placeholder = "",
  disabled = false,
  onChange,
  classes: { wrapper = "", input = "" } = {},
  autoComplete = "on",
  autoFocus,
  inputRef,
  error,
  ...rest
}) => {
  const identifier = id || inputKey;
  const onInputChange = (ev) => onChange(ev.target.value, identifier, ev);

  return (
    <div className={`form-group ${wrapper}`}>
      {prefix}
      <div className="w-100">
        <textarea
          placeholder={placeholder}
          className={`form-control ${input} ${error ? "input-error" : ""}`}
          id={identifier}
          disabled={disabled}
          value={value}
          onChange={onInputChange}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          ref={inputRef}
          {...rest}
        />
        <div className="input-animation"></div>
      </div>
      {suffix}
      {error && <span className="error-message text-danger py-2">{error}</span>}
    </div>
  );
};

export const RadioField = ({
  id,
  name,
  children,
  onChange,
  checked,
  classes: { wrapper = "", input = "", label = "" },
}) => {
  const onRadioChange = (ev) => onChange(id, ev.target.checked, ev);
  return (
    <div className={wrapper}>
      <input
        id={id}
        type="radio"
        name={name}
        className={input}
        onChange={onRadioChange}
        checked={checked}
      />
      <label htmlFor={id} className={label}>
        {children}
      </label>
    </div>
  );
};

export const CheckboxField = ({
  id = "",
  disabled = false,
  checked = false,
  onChange = () => {},
  customClass = "",
}) => (
  <input
    id={id}
    type="checkbox"
    className={customClass}
    disabled={disabled}
    checked={checked}
    {...(onChange && { onChange })}
  />
);
